import { createSlice } from '@reduxjs/toolkit';

// The duty-roster draft queue. Every rota edit (cover, team move, rename,
// remove-day, attendance review, holiday) is appended here as a "change"
// instead of hitting the API. The grid renders the server snapshot with this
// queue applied on top, and Publish replays the queue against the API.
//
// A change looks like:
//   { id, type, payload, label, dedupKey? }
// where `type` is one of:
//   cover | reassign | rename | removeDay | review | absence | removeAbsence
// `label` is a human-readable summary for the draft list, and `dedupKey`
// (optional) lets a newer edit of the same target replace an earlier one.
const initialState = {
    changes: [],
};

const rosterDraftSlice = createSlice({
    name: 'rosterDraft',
    initialState,
    reducers: {
        // Append a change. If it carries a dedupKey that matches an existing
        // queued change, the new one replaces it (e.g. re-editing the same
        // cover/date keeps a single entry).
        enqueueChange(state, action) {
            const change = action.payload;
            if (change.dedupKey) {
                const idx = state.changes.findIndex((c) => c.dedupKey === change.dedupKey);
                if (idx >= 0) {
                    state.changes[idx] = change;
                    return;
                }
            }
            state.changes.push(change);
        },
        // Remove a single queued change by its id.
        removeChange(state, action) {
            state.changes = state.changes.filter((c) => c.id !== action.payload);
        },
        // Replace the whole queue (used after a partial publish to keep only the
        // changes that failed to sync).
        setChanges(state, action) {
            state.changes = action.payload;
        },
        // Drop every queued change (Discard / after a clean publish).
        clearChanges(state) {
            state.changes = [];
        },
    },
});

export const { enqueueChange, removeChange, setChanges, clearChanges } = rosterDraftSlice.actions;
export default rosterDraftSlice.reducer;
